import type { ProductWrapperType } from '@/api/generated/model';
import { type Account } from '@/api/generated/model';
import { Button } from '@/components/ui/button';
import { EntitySelect } from '@/components/ui/entity-select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { WRAPPER_TYPE_LABELS, WRAPPER_TYPES } from '@/features/investing/productWrapperDefaults';
import { usePartyCreateDialog } from '@/features/parties/usePartyCreateDialog';
import { useLanguage } from '@/i18n/language-hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const creditDetailsSchema = z.object({
  creditLimit: z.number().min(0, 'Credit limit must be positive'),
  aprPercent: z.number().min(0).max(100),
  billingCycleDay: z.number().int().min(1).max(28),
  minimumPayment: z.number().min(0),
});

const wrapperTypeSchema = z.enum(['isa', 'lisa', 'sipp', 'gia', 'juniorIsa', 'pension']);

const baseAccountFields = {
  name: z.string().min(1, 'Name is required'),
  type: z.enum([
    'bank',
    'checking',
    'savings',
    'investment',
    'retirement',
    'real-estate',
    'crypto',
    'alternative',
    'liability',
  ]),
  ownerId: z.string().optional(),
  balance: z
    .number()
    .min(-1000000000000, 'Balance must be more than -1 trillion')
    .max(1000000000000, 'Balance must be less than 1 trillion'),
  institution: z.string().optional(),
  creditDetails: creditDetailsSchema.optional(),
  wrapperType: wrapperTypeSchema.optional(),
};

function buildAccountFormSchema(enableProductWrapper: boolean) {
  return z.object(baseAccountFields).superRefine((data, ctx) => {
    if (enableProductWrapper && !data.wrapperType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Tax wrapper type is required',
        path: ['wrapperType'],
      });
    }
    if (data.type === 'liability' && !data.creditDetails) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Credit details required for liability accounts',
        path: ['creditDetails', 'creditLimit'],
      });
    }
  });
}

export type AccountFormValues = z.infer<ReturnType<typeof buildAccountFormSchema>>;

const ALL_ACCOUNT_TYPES: AccountFormValues['type'][] = [
  'bank',
  'checking',
  'savings',
  'investment',
  'retirement',
  'real-estate',
  'crypto',
  'alternative',
  'liability',
];

interface AccountFormProps {
  initialData?: Account;
  onSubmit: (data: AccountFormValues) => Promise<void>;
  onCancel: () => void;
  allowedTypes?: AccountFormValues['type'][];
  enableProductWrapper?: boolean;
}

export function AccountForm({
  initialData,
  onSubmit,
  onCancel,
  allowedTypes,
  enableProductWrapper = false,
}: AccountFormProps) {
  const { t } = useLanguage();
  const schema = useMemo(
    () => buildAccountFormSchema(enableProductWrapper),
    [enableProductWrapper]
  );

  const form = useForm<AccountFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialData
      ? {
          name: initialData.name,
          type: initialData.type as AccountFormValues['type'],
          ownerId: initialData.ownerId || '',
          balance: initialData.balance,
          institution: initialData.institution || '',
          creditDetails: initialData.creditDetails,
          ...(enableProductWrapper
            ? {
                wrapperType: (initialData.productWrapper?.wrapperType ??
                  'isa') as ProductWrapperType,
              }
            : {}),
        }
      : {
          name: '',
          type: allowedTypes?.[0] ?? 'bank',
          ownerId: '',
          balance: 0,
          institution: '',
          creditDetails: undefined,
          ...(enableProductWrapper ? { wrapperType: 'isa' as ProductWrapperType } : {}),
        },
  });

  const { parties, openCreateParty } = usePartyCreateDialog({
    onPartyCreated: (partyId) => form.setValue('ownerId', partyId),
  });

  const ownerId = form.watch('ownerId');
  const ownerSelectItems = useMemo(() => {
    if (!ownerId || parties.some((party) => party.id === ownerId)) {
      return parties;
    }
    return [
      ...parties,
      {
        id: ownerId,
        name: t('accounts.form.ownerUnknown'),
        type: 'Individual' as const,
        createdAt: '',
        updatedAt: '',
      },
    ];
  }, [ownerId, parties, t]);

  useEffect(() => {
    if (!ownerId && parties.length > 0) {
      form.setValue('ownerId', parties[0]!.id);
    }
  }, [form, ownerId, parties]);

  const selectableTypes = allowedTypes ?? ALL_ACCOUNT_TYPES;
  const accountType = form.watch('type');
  const showCreditFields = accountType === 'liability';

  useEffect(() => {
    if (accountType === 'liability' && !form.getValues('creditDetails')) {
      form.setValue('creditDetails', {
        creditLimit: 0,
        aprPercent: 0,
        billingCycleDay: 1,
        minimumPayment: 0,
      });
    } else if (accountType !== 'liability' && form.getValues('creditDetails')) {
      form.setValue('creditDetails', undefined);
    }
  }, [accountType, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
        <FormField
          control={form.control}
          name='name'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('accounts.form.name')}</FormLabel>
              <FormControl>
                <Input placeholder={t('accounts.form.namePlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='type'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('accounts.form.type')}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('accounts.form.typePlaceholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {selectableTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`accounts.types.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {enableProductWrapper ? (
          <FormField
            control={form.control}
            name='wrapperType'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('investing.wrapperType')}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('investing.wrapperTypePlaceholder')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {WRAPPER_TYPES.map((wrapperType) => (
                      <SelectItem key={wrapperType} value={wrapperType}>
                        {t(`investing.wrapperTypes.${wrapperType}`, {
                          defaultValue: WRAPPER_TYPE_LABELS[wrapperType],
                        })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}

        <FormField
          control={form.control}
          name='ownerId'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('accounts.form.owner')}</FormLabel>
              <FormControl>
                <EntitySelect
                  items={ownerSelectItems}
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  getItemValue={(party) => party.id}
                  renderItem={(party) => `${party.name} (${party.type})`}
                  placeholder={t('accounts.form.ownerPlaceholder')}
                  addNewLabel={t('common.addNewParty')}
                  onAddNew={() => openCreateParty()}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='balance'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('accounts.form.balance')}</FormLabel>
              <FormControl>
                <Input
                  type='number'
                  step='0.01'
                  placeholder={t('accounts.form.balancePlaceholder')}
                  value={Number.isFinite(field.value) ? field.value : ''}
                  onChange={(e) => {
                    const parsed = parseFloat(e.target.value);
                    field.onChange(Number.isFinite(parsed) ? parsed : 0);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='institution'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('accounts.form.institution')}</FormLabel>
              <FormControl>
                <Input placeholder={t('accounts.form.institutionPlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {showCreditFields && (
          <div className='border-fin-gray-800 space-y-4 rounded-lg border p-4'>
            <p className='text-sm font-medium'>{t('accounts.form.creditSection')}</p>
            <FormField
              control={form.control}
              name='creditDetails.creditLimit'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('accounts.form.creditLimit')}</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      step='0.01'
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? undefined : parseFloat(e.target.value)
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='creditDetails.aprPercent'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('accounts.form.aprPercent')}</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      step='0.1'
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? undefined : parseFloat(e.target.value)
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='creditDetails.billingCycleDay'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('accounts.form.billingCycleDay')}</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      step='1'
                      min={1}
                      max={28}
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? undefined : parseInt(e.target.value, 10)
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='creditDetails.minimumPayment'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('accounts.form.minimumPayment')}</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      step='0.01'
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? undefined : parseFloat(e.target.value)
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <div className='flex justify-end space-x-2'>
          <Button type='button' variant='outline' onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type='submit'>{initialData ? t('common.save') : t('common.create')}</Button>
        </div>
      </form>
    </Form>
  );
}
